import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  AlertTriangle, Clock, Calendar, ArrowRight, 
  ExternalLink, Info, XCircle
} from 'lucide-react';
import { DEPRECATION_NOTICES, getActiveDeprecations, getDocDeprecation } from '../../data/governanceContent';

/**
 * DeprecationNotice - Inline deprecation warning for documents
 */
export function DeprecationNotice({ docId, variant = 'default' }) {
  const deprecation = getDocDeprecation(docId);

  if (!deprecation) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'announced': return 'bg-amber-50 border-amber-300 text-amber-800';
      case 'deprecated': return 'bg-orange-50 border-orange-300 text-orange-800';
      case 'removed': return 'bg-red-50 border-red-300 text-red-800';
      default: return 'bg-gray-50 border-gray-300 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'announced': return Info;
      case 'deprecated': return AlertTriangle;
      case 'removed': return XCircle;
      default: return AlertTriangle;
    }
  };

  const StatusIcon = getStatusIcon(deprecation.status);

  if (variant === 'compact') {
    return (
      <Badge className={`${getStatusColor(deprecation.status)} gap-1`}>
        <StatusIcon className="w-3 h-3" />
        {deprecation.status}
      </Badge>
    );
  }

  return (
    <Card className={`${getStatusColor(deprecation.status)} border-2`} data-testid="deprecation-notice">
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          <StatusIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">{deprecation.title}</h4>
              <Badge variant="outline" className="capitalize">
                {deprecation.status}
              </Badge>
            </div>
            <p className="text-sm mb-3">{deprecation.reason}</p>
            
            {/* Timeline */}
            <div className="flex flex-wrap gap-4 text-xs mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>Announced: {deprecation.announcedDate}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>Deprecated: {deprecation.deprecationDate}</span>
              </div>
              <div className="flex items-center gap-1 font-semibold">
                <XCircle className="w-3 h-3" />
                <span>Removal: {deprecation.removalDate}</span>
              </div>
            </div>

            {/* Migration link */}
            {deprecation.migration && (
              <Button variant="outline" size="sm" className="gap-2" asChild>
                <a href={deprecation.migration.link}>
                  View Migration Guide
                  <ArrowRight className="w-3 h-3" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * DeprecationBanner - Site-wide banner for critical deprecations
 */
export function DeprecationBanner({ onDismiss }) {
  const activeDeprecations = getActiveDeprecations();
  const criticalDeprecations = activeDeprecations.filter(d => d.status === 'deprecated');

  if (criticalDeprecations.length === 0) return null;

  return (
    <div className="bg-amber-500 text-white px-4 py-3" data-testid="deprecation-banner">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm font-medium">
            {criticalDeprecations.length} deprecated feature{criticalDeprecations.length > 1 ? 's' : ''} will be removed soon. 
          </span>
          <Button variant="secondary" size="sm" className="h-7 text-xs bg-white/20 hover:bg-white/30 border-0">
            View Details
          </Button>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="hover:bg-white/20 p-1 rounded">
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * DeprecationList - Shows all deprecation notices
 */
export function DeprecationList() {
  const deprecations = DEPRECATION_NOTICES;

  if (deprecations.length === 0) {
    return (
      <Card className="text-center py-8">
        <CardContent>
          <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-700 mb-2">No Deprecations</h3>
          <p className="text-sm text-gray-500">
            There are currently no deprecated features or documentation sections.
          </p>
        </CardContent>
      </Card>
    );
  }

  const groupByStatus = () => {
    const groups = { announced: [], deprecated: [], removed: [] };
    deprecations.forEach(d => {
      if (groups[d.status]) {
        groups[d.status].push(d);
      }
    });
    return groups;
  };

  const grouped = groupByStatus();

  return (
    <div className="space-y-6" data-testid="deprecation-list">
      {/* Active Deprecations */}
      {(grouped.announced.length > 0 || grouped.deprecated.length > 0) && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Active Notices
          </h3>
          <div className="space-y-3">
            {[...grouped.announced, ...grouped.deprecated].map(dep => (
              <DeprecationNotice key={dep.id} docId={dep.affectedDocs[0]} />
            ))}
          </div>
        </div>
      )}

      {/* Removed (Historical) */}
      {grouped.removed.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-500 mb-3 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Removed (Historical)
          </h3>
          <div className="space-y-3 opacity-60">
            {grouped.removed.map(dep => (
              <Card key={dep.id} className="bg-gray-50">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">{dep.title}</span>
                    <Badge variant="outline" className="text-gray-500">
                      Removed {dep.removalDate}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * MigrationGuide - Detailed migration steps for deprecated features
 */
export function MigrationGuide({ deprecationId }) {
  const deprecation = DEPRECATION_NOTICES.find(d => d.id === deprecationId);

  if (!deprecation || !deprecation.migration) {
    return null;
  }

  return (
    <Card data-testid="migration-guide">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRight className="w-5 h-5 text-indigo-600" />
          {deprecation.migration.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {deprecation.migration.steps.map((step, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-semibold text-sm flex-shrink-0">
                {idx + 1}
              </div>
              <span className="text-gray-700">{step}</span>
            </li>
          ))}
        </ol>

        {deprecation.migration.link && (
          <Button variant="outline" className="mt-4 gap-2" asChild>
            <a href={deprecation.migration.link}>
              <ExternalLink className="w-4 h-4" />
              View Full Documentation
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default DeprecationNotice;
